<template>
  <div class="mpids-page">
    <!-- Background div that spans from left edge to right of filters column -->
    <div class="bg-section">
      <div ref="mpidMap" class="mpid-map"></div>
      <div class="map-overlay-red-50"></div>

    </div>

    <!-- Filter Overlay component - currently inactive but kept for future implementation -->
    <FilterOverlay
        :is-visible="showFilterOverlay"
        :filter-type="activeFilterForOverlay"
        :initial-filter-options="currentFilterOptions"
        @close="closeFilterOverlay"
        @apply="applyAdvancedFilters"
    />
    <!-- Add MPID Overlay component -->
    <AddMpidOverlay
        :is-visible="showAddMpidOverlay"
        @close="closeAddMpidOverlay"
        @success="onMpidAddSuccess"
    />
    <ImportMpidOverlay
        :is-visible="showImportMpidOverlay"
        @close="closeImportMpidOverlay"
        @submit="handleImportMpids"
    />

    <AppHeader title="Metering Points" />
    <div class="content">
      <div class="search-container">
        <!-- SEARCH BY section -->
        <div class="search-column">
          <h2 class="section-title">SEARCH BY :</h2>
          <div class="search-options">
            <button
                v-for="option in searchOptions"
                :key="option.id"
                :class="['search-option', { active: activeSearchOption === option.id }]"
                @click="toggleSearchOption(option.id)"
            >
              {{ option.label }}
            </button>
          </div>
          <div class="search-input-container">
            <input
                type="text"
                class="search-input"
                placeholder="Search..."
                v-model="searchQuery"
                @input="handleSearchInput"
            />
            <button class="search-button" @click="performSearch">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- FILTERS section -->
        <div class="filters-column">
          <h2 class="section-title">FILTERS :</h2>
          <div class="filter-sections">
            <div class="main-filters">
              <button
                  v-for="filter in filterOptions"
                  :key="filter.id"
                  :class="['filter-option', { active: activeFilterOptions.includes(filter.id), 'has-advanced-filters': hasAdvancedFilters(filter.id) }]"
                  @click="toggleFilterOption(filter.id)"
                  disabled
              >
                {{ filter.label }}
                <span
                    v-if="hasAdvancedFilters(filter.id)"
                    class="filter-indicator"
                    @click.stop="openFilterOverlay(filter.id)"
                ></span>
              </button>
            </div>
            <div class="sub-filters">
              <button
                  v-for="subFilter in subFilterOptions"
                  :key="subFilter.id"
                  :class="['sub-filter-option', 'disabled']"
                  disabled
              >
                {{ subFilter.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Action buttons section -->
        <div class="actions-column">
          <div class="action-buttons">
            <button class="action-button" @click="openAddMpidOverlay">
              <span class="plus-icon">+</span> Add MPID
            </button>
            <button class="action-button" @click="exportMpids" :disabled="isExporting" :aria-busy="isExporting">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {{ isExporting ? 'Exporting...' : 'Export MPIDs' }}
            </button>
            <button class="action-button" @click="openImportMpidOverlay">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                   stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Import MPIDs
            </button>
          </div>
        </div>
      </div>

      <!-- MPIDs Table -->
      <MpidsTable
          :data="paginatedMpids"
          :columns="tableColumns"
          :loading="isLoading"
          :sort-column="sortColumn"
          :sort-direction="sortDirection"
          :pagination="true"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-items="totalItems"
          :selected-row="selectedMpid"
          row-key="mpid"
          @sort="handleSort"
          @row-select="selectMpid"
          @row-hover="handleRowHover"
          @row-leave="handleRowLeave"
          @page-change="goToPage"
      />
    </div>
  </div>
</template>

<script>
import Papa from 'papaparse';
import AppHeader from './AppHeader.vue';
import MpidsTable from './MpidTable.vue';
import FilterOverlay from './MpidFilterOverlay.vue';
import { debounce } from 'lodash';
import MeteringpointApiService from '@/services/MpidApiService';
import AddMpidOverlay from './MpidAddOverlay.vue';
import ImportMpidOverlay from './MpidImportOverlay.vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export default {
  name: 'MpidPage',
  components: {
    AppHeader,
    MpidsTable,
    FilterOverlay,
    AddMpidOverlay,
    ImportMpidOverlay
  },
  data() {
    return {
      searchOptions: [
        { id: 'city', label: 'CITY' },
        { id: 'mpid', label: 'MPID' },
        { id: 'customer', label: 'CUSTOMER' }
      ],
      filterOptions: [
        { id: 'hasDevice', label: 'HAS DEVICE' },
        { id: 'noDevice', label: 'NO DEVICE' }
      ],
      subFilterOptions: [
        { id: 'city', label: 'City' },
        { id: 'street', label: 'Street' }
      ],
      activeSearchOption: '',
      activeFilterOptions: [],
      activeSubFilter: '',
      searchQuery: '',
      tableColumns: [
        { id: 'mpid', label: 'MPID', sortable: true },
        { id: 'street', label: 'STREET', sortable: true },
        { id: 'city', label: 'CITY', sortable: true },
        { id: 'zipCode', label: 'ZIP CODE', sortable: true },
        { id: 'country', label: 'COUNTRY', sortable: true },
        { id: 'customer', label: 'CUSTOMER', sortable: true },
        { id: 'device_attached', label: 'DEVICE', sortable: true },
        { id: 'lastUpdate', label: 'LAST UPDATE', sortable: true, type: 'date' }
      ],
      sortColumn: 'mpid',
      sortDirection: 'asc',
      filteredMpids: [],
      paginatedMpids: [],
      isLoading: true,
      isExporting: false,
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      selectedMpid: null,
      showFilterOverlay: false,
      showAddMpidOverlay: false,
      showImportMpidOverlay: false,
      activeFilterForOverlay: '',
      advancedFilterOptions: {
        hasDevice: { deviceType: '', lastReading: '' },
        noDevice: { moreThanDays: '' }
      },
      filtersWithAdvancedOptions: [],
      map: null,
      mapSourceLoaded: false,
      hoveredMpidId: null,
      initialMapFitted: false,
      lastHoverMoveAt: 0,
      hoverMoveMinIntervalMs: 120
    };
  },
  computed: {
    currentFilterOptions() {
      return this.advancedFilterOptions[this.activeFilterForOverlay] || {};
    }
  },
  watch: {
    activeFilterOptions: 'fetchMpids',
    activeSubFilter: 'fetchMpids',
    sortColumn: 'fetchMpids',
    sortDirection: 'fetchMpids',
    currentPage: 'fetchMpids'
  },
  methods: {
    toggleSearchOption(optionId) {
      this.activeSearchOption = this.activeSearchOption === optionId ? null : optionId;
      this.performSearch();
    },
    toggleFilterOption(filterId) {
      if (this.activeFilterOptions.includes(filterId)) {
        this.activeFilterOptions = this.activeFilterOptions.filter(id => id !== filterId);
        this.filtersWithAdvancedOptions = this.filtersWithAdvancedOptions.filter(id => id !== filterId);
      } else {
        this.activeFilterOptions.push(filterId);
        this.openFilterOverlay(filterId);
      }
      this.currentPage = 1;
    },
    setActiveSubFilter(filterId) {
      this.activeSubFilter = filterId;
      this.fetchMpids();
    },
    handleSearchInput: debounce(function () {
      this.performSearch();
    }, 300),
    performSearch() {
      this.currentPage = 1;
      this.fetchMpids();
    },
    getMpidSearchParams() {
      const params = {};
      if (this.searchQuery && ['city', 'mpid'].includes(this.activeSearchOption)) {
        params[this.activeSearchOption] = this.searchQuery;
      }
      return params;
    },
    async exportMpids() {
      if (this.isExporting) return;
      this.isExporting = true;
      try {
        const result = await MeteringpointApiService.getMeteringPoints({
          ...this.getMpidSearchParams(),
          pagination: false
        });
        const rows = result?.data ?? [];
        if (!Array.isArray(rows) || Number(result?.meta?.total ?? rows.length) > rows.length) {
          throw new Error('The server returned an incomplete export.');
        }
        if (!rows.length) {
          alert('No metering points found to export with the current search.');
          return;
        }
        const csv = Papa.unparse({
          fields: ['MPID', 'STREET', 'CITY', 'ZIP CODE', 'COUNTRY', 'CUSTOMER', 'DEVICE', 'LATITUDE', 'LONGITUDE', 'LAST UPDATE'],
          data: rows.map(row => {
            const device = row.last_active_device ?? row.lastActiveDevice;
            return [
              row.mpid ?? '', row.street ?? '', row.city ?? '',
              row.zip_code ?? row.zipCode ?? '', row.country ?? '',
              row.customer_fname ?? row.customerFName ?? '',
              device?.logical_device_number ?? device?.ldn ?? '',
              row.lat ?? '', row.lon ?? '',
              row.updated_at ?? row.updatedAt ?? row.UpdatedAt ?? row.update_date ?? ''
            ];
          })
        }, { escapeFormulae: true });
        const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `mpids_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
        try {
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      } catch (error) {
        console.error('Error exporting metering points:', error);
        alert('Failed to export MPIDs. Please try again.');
      } finally {
        this.isExporting = false;
      }
    },
    async fetchMpids() {
      this.isLoading = true;
      try {
        const params = {
          ...this.getMpidSearchParams(),
          page: this.currentPage,
          limit: this.pageSize
        };

        // Call service: must forward params as query string
        const apiResult = await MeteringpointApiService.getMeteringPoints(params);
        // apiResult should be { data: [...], meta: { total, page, limit, totalPages } }

        const rows = (apiResult?.data || []).map(mpid => ({
          ...mpid,
          zipCode: mpid.zipCode || mpid.zip_code || '',
          city: mpid.city || '',
          street: mpid.street || '',
          customer: mpid.customerFName || mpid.customer_fname || '',
          country: mpid.country || '',
          deviceCount: mpid.lastActiveDevice ? 1 : (mpid.last_active_device ? 1 : 0),
          lastUpdate: mpid.updated_at || mpid.updatedAt || mpid.UpdatedAt || mpid.update_date || ''
        }));

        this.paginatedMpids = rows;
        this.filteredMpids = rows; // used for the map layer
        this.totalItems = apiResult?.meta?.total ?? rows.length;
        this.currentPage = apiResult?.meta?.page ?? this.currentPage;
        this.pageSize = apiResult?.meta?.limit ?? this.pageSize;

        this.$nextTick(() => this.updateMapData());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching metering points:', error);
      } finally {
        this.isLoading = false;
      }
    },
    createAdvancedFiltersObject() {
      const advancedFilters = {};
      this.activeFilterOptions.forEach(filterType => {
        if (this.hasAdvancedFilters(filterType)) {
          advancedFilters[filterType] = { ...this.advancedFilterOptions[filterType] };
        }
      });
      return advancedFilters;
    },
    handleSort({ column, direction }) {
      this.sortColumn = column;
      this.sortDirection = direction;
      // For server-side sorting, add sort params to fetch (if backend supports)
      // this.currentPage = 1;
      // this.fetchMpids();
    },
    goToPage(page) {
      if (page !== this.currentPage) this.currentPage = page;
    },
    selectMpid(mpid) {
      this.selectedMpid = mpid;
    },
    openFilterOverlay(filterId) {
      this.activeFilterForOverlay = filterId;
      this.showFilterOverlay = true;
    },
    closeFilterOverlay() {
      this.showFilterOverlay = false;
    },
    applyAdvancedFilters({ filterType, options }) {
      this.advancedFilterOptions[filterType] = { ...options };
      if (!this.filtersWithAdvancedOptions.includes(filterType)) {
        this.filtersWithAdvancedOptions.push(filterType);
      }
      if (!this.activeFilterOptions.includes(filterType)) {
        this.activeFilterOptions.push(filterType);
      }
      this.fetchMpids();
    },
    hasAdvancedFilters(filterId) {
      return this.filtersWithAdvancedOptions.includes(filterId);
    },
    openAddMpidOverlay() {
      this.showAddMpidOverlay = true;
    },
    closeAddMpidOverlay() {
      this.showAddMpidOverlay = false;
    },
    openImportMpidOverlay() {
      this.showImportMpidOverlay = true;
    },
    closeImportMpidOverlay() {
      this.showImportMpidOverlay = false;
    },
    async handleImportMpids(mpids) {
      try {
        // eslint-disable-next-line no-console
        console.log('Importing MPIDs:', mpids);
        this.fetchMpids();
        this.closeImportMpidOverlay();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error importing MPIDs:', error);
      }
    },
    onMpidAddSuccess(mpidData) {
      // eslint-disable-next-line no-console
      console.log('MPID added successfully:', mpidData);
      this.fetchMpids();
    },

    // ---------------- Map ----------------
    initializeMap() {
      if (this.map) return;

      const STYLE_URL = 'https://api.maptiler.com/maps/basic-v2-dark/style.json?key=4DXCyU0jQcYTU9cFAGQu';

      this.map = new maplibregl.Map({
        container: this.$refs.mpidMap,
        style: STYLE_URL,
        center: [9.1859, 45.4654],
        zoom: 4,
        attributionControl: false
      });

      this.map.dragRotate.enable();
      this.map.touchZoomRotate.enableRotation();

      this.map.on('load', async () => {
        const layers = (this.map.getStyle() && this.map.getStyle().layers) || [];
        layers.forEach(l => {
          const srcLayer = l['source-layer'] || '';
          if (
              l.type === 'symbol' &&
              (
                  /place|settlement/i.test(srcLayer) ||
                  /place|city|town|village|hamlet|settlement/i.test(l.id)
              )
          ) {
            this.map.setLayoutProperty(l.id, 'visibility', 'none');
          }
        });

        this.map.addSource('mpids', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          cluster: true,
          clusterRadius: 50
        });

        this.map.addLayer({
          id: 'mpid-clusters',
          type: 'circle',
          source: 'mpids',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#4fc3f7',
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              16,
              50, 22,
              150, 28
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.85
          }
        });

        this.map.addLayer({
          id: 'mpid-cluster-count',
          type: 'symbol',
          source: 'mpids',
          filter: ['has', 'point_count'],
          layout: { 'text-field': '{point_count_abbreviated}', 'text-size': 12 },
          paint: { 'text-color': '#ffffff' }
        });

        this.map.addLayer({
          id: 'mpid-point',
          type: 'circle',
          source: 'mpids',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': [
              'case',
              ['boolean', ['feature-state', 'hovered'], false],
              '#ffffff',
              '#4fc3f7'
            ],
            'circle-radius': [
              'case',
              ['boolean', ['feature-state', 'hovered'], false],
              10,
              6
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': [
              'case',
              ['boolean', ['feature-state', 'hovered'], false],
              '#4fc3f7',
              '#ffffff'
            ],
            'circle-opacity': 0.95
          }
        });

        this.map.on('click', 'mpid-clusters', async (e) => {
          const features = this.map.queryRenderedFeatures(e.point, { layers: ['mpid-clusters'] });
          if (!features.length) return;
          const clusterId = features[0].properties.cluster_id;
          const source = this.map.getSource('mpids');
          try {
            const zoom = await source.getClusterExpansionZoom(clusterId);
            this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.debug('Cluster expansion failed:', err);
            }
          }
        });

        this.map.on('mouseenter', 'mpid-clusters', () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', 'mpid-clusters', () => {
          this.map.getCanvas().style.cursor = '';
        });

        this.mapSourceLoaded = true;
        this.updateMapData();
      });
    },

    updateMapData() {
      if (!this.map || !this.mapSourceLoaded) return;
      const source = this.map.getSource('mpids');
      if (!source) return;

      const features = (this.filteredMpids || [])
          .filter(p =>
              p && p.lon !== undefined && p.lat !== undefined &&
              !isNaN(parseFloat(p.lon)) && !isNaN(parseFloat(p.lat))
          )
          .map(p => ({
            type: 'Feature',
            id: p.mpid || `${p.lon},${p.lat}`,
            geometry: {
              type: 'Point',
              coordinates: [parseFloat(p.lon), parseFloat(p.lat)]
            },
            properties: {
              mpid: p.mpid || '',
              street: p.street || '',
              city: p.city || '',
              customer: p.customer || '',
              lastUpdate: p.lastUpdate || ''
            }
          }));

      source.setData({ type: 'FeatureCollection', features });

      if (features.length && !this.initialMapFitted) {
        const bounds = new maplibregl.LngLatBounds();
        features.forEach(f => bounds.extend(f.geometry.coordinates));
        this.map.fitBounds(bounds, {
          padding: { top: 60, bottom: 60, left: 60, right: 60 },
          maxZoom: 12,
          duration: 800
        });
        this.initialMapFitted = true;
      }

      if (this.hoveredMpidId) {
        const stillExists = features.some(f => f.id === this.hoveredMpidId);
        if (!stillExists) {
          this.clearHoverState();
        } else {
          this.setHoverState(this.hoveredMpidId, true);
        }
      }
    },
    setHoverState(id, hovered) {
      if (!this.map || !this.mapSourceLoaded) return;
      if (this.hoveredMpidId && this.hoveredMpidId !== id) {
        this.map.setFeatureState({ source: 'mpids', id: this.hoveredMpidId }, { hovered: false });
      }
      if (id != null) {
        this.map.setFeatureState({ source: 'mpids', id }, { hovered });
        this.hoveredMpidId = hovered ? id : null;
      }
    },
    clearHoverState() {
      if (!this.map || !this.mapSourceLoaded) return;
      if (this.hoveredMpidId) {
        this.map.setFeatureState({ source: 'mpids', id: this.hoveredMpidId }, { hovered: false });
        this.hoveredMpidId = null;
      }
    },
    handleRowHover(item) {
      if (!item || !this.map) return;
      const id = item.mpid || null;
      if (!id) return;

      this.setHoverState(id, true);

      if (item.lon != null && item.lat != null) {
        const now = performance.now();
        if (now - this.lastHoverMoveAt < this.hoverMoveMinIntervalMs) return;
        this.lastHoverMoveAt = now;

        const lng = parseFloat(item.lon);
        const lat = parseFloat(item.lat);
        if (!isNaN(lng) && !isNaN(lat)) {
          const targetZoom = Math.max(this.map.getZoom(), 17);

          this.map.flyTo({
            center: [lng, lat],
            zoom: targetZoom,
            bearing: this.map.getBearing(),
            speed: 1.5,
            curve: 1.5,
            essential: false
          });
        }
      }
    },
    handleRowLeave() {
      this.clearHoverState();
    }
  },
  mounted() {
    this.initializeMap();
    this.fetchMpids();
  }
};
</script>


<style scoped>
.mpids-page {
  min-height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;
  position: relative;
}


/* Background section that spans from left edge to right of filters column */
.bg-section {
  position: absolute;
  top: 0;
  left: 33%; /* Start from roughly where the filters column begins */
  width: 67%; /* Cover the rest of the screen */
  height: 100%;
  background-color: rgba(86, 86, 86, 0.1); /* #565656 with 10% opacity */
  z-index: 0;
}

.mpid-map {
  position: absolute;
  inset: 0;
}



/* Keep map controls above */
:deep(.maplibregl-ctrl) {
  z-index: 2;
}


.content {
  padding: 100px 110px;
  position: relative;
  z-index: 1;
}


.search-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 24px;
  text-transform: uppercase;
  text-align: left;
}

/* Search Column */
.search-column {
  flex: 1;
}

.search-options {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding-left: 25px;
}

.search-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
  display: flex;
  align-items: center;
}

.search-option:hover,
.search-option.active {
  opacity: 1;
}

.search-input-container {
  position: relative;
  max-width: 300px;
  padding-left: 25px;
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 12px;
}

.search-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.search-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  cursor: pointer;
}

/* Filters Column */
.filters-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.filter-sections {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 25px;
}

.main-filters {
  display: flex;
  justify-content: start;
  gap: 24px;
  margin-bottom: 16px;
  width: 100%;
}

.filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
  display: flex;
  align-items: center;
}

.filter-option:hover,
.filter-option.active {
  opacity: 1;
}

/* Advanced filter indicator dot */
.filter-indicator {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #4fc3f7;
  border-radius: 50%;
  margin-left: 6px;
  cursor: pointer;
}

.filter-option.has-advanced-filters::after {
  content: '';
}

.sub-filters {
  display: flex;
  justify-content: start;
  gap: 32px;
  width: 100%;
  margin-top: 0;
  padding-bottom: 16px;
}

.sub-filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.05em;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.sub-filter-option.disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.sub-filter-option:hover,
.sub-filter-option.active {
  opacity: 1;
}

/* Actions Column */
.actions-column {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.plus-icon {
  font-size: 16px;
  font-weight: bold;
}
.map-overlay-red-50 {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      to bottom,
      rgba(27, 26, 26, 0.85) 0%,   /* frosted at top */ rgba(23, 23, 23, 0.45) 100%  /* fully transparent at bottom */
  );
  backdrop-filter: blur(0px) ;
  -webkit-backdrop-filter: blur(0px);
  pointer-events: none; /* keep map interactive */
}
</style>
